import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  Chip,
  Avatar,
  Grid,
  LinearProgress,
  Button,
  useTheme,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Pagination,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Close,
  LockClock,
  PlayCircle,
  StopCircle,
  Phone,
  Email,
  CameraAlt,
  LocationOn,
  AttachMoney,
  ContentCopy,
  EuroSymbol
} from '@mui/icons-material';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';

import { Props } from './interface';
import { TripCoordinatesHistory } from '../TripCoordinatesModal/TripCoordinatesHistory';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { formatDate } from '../../resources/functions';
import axios from '../../resources/axios';
import TableCell from '../../components/TableCell';
import { getContractsHistory } from '../../redux/slices/contractsSlice';
import { RootState } from '../../redux/store';
import { convertSecondsToHHMMSS } from '../../helpers/time';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trip-tabpanel-${index}`}
      aria-labelledby={`trip-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const InfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
  borderRadius: '12px',
  padding: '20px',
  height: '100%'
}));

const InfoRow = ({ 
  label, 
  value, 
  icon, 
  copyValue, 
  onCopy 
}: { 
  label: string; 
  value: string | React.ReactNode; 
  icon?: React.ReactNode;
  copyValue?: string;
  onCopy?: (text: string) => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" color="textSecondary">
          {label}:
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body1" fontWeight={500}>
          {value}
        </Typography>
        {copyValue && onCopy && (
          <Tooltip title={t('trips.details.copy')}>
            <IconButton
              size="small"
              onClick={() => onCopy(copyValue)}
              sx={{
                padding: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const BatteryIndicator = ({ label, percentage, color }: { label: string; percentage: number; color: string }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: color,
            border: '2px solid',
            borderColor: color
          }}
        />
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ color }}>
          {percentage}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4
          }
        }}
      />
    </Box>
  );
};

export const TripDetailsModal = (props: Props) => {
  const { tripId, tripData: initialTripData, onClose, onRefundClick, onUserHistoryClick, onEditReportAmount, ...restProps } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [tripData, setTripData] = useState<any>(initialTripData || null);
  const [loading, setLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);

  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const { items: historyContracts, pages: historyPages } = useSelector(
    (state: RootState) => state.contracts.history
  );

  const cityId = useMemo(() => {
    return tripData?.city?._id || adminInfo.currentCity;
  }, [adminInfo.currentCity, tripData]);

  const cityCoords = useMemo(() => {
    const city = cities.items.find(item => item._id === cityId);
    if (city) {
      return {
        lat: city.lat as number,
        lng: city.lon as number
      };
    }
  }, [cities.items, cityId]);

  useEffect(() => {
    if (initialTripData) {
      console.log('TripDetailsModal - Full tripData:', initialTripData);
      console.log('TripDetailsModal - city:', initialTripData.city);
      console.log('TripDetailsModal - user:', initialTripData.user);
      console.log('TripDetailsModal - sum:', initialTripData.sum);
      console.log('TripDetailsModal - historyDetails:', initialTripData.historyDetails);
      console.log('TripDetailsModal - scooter.battery:', initialTripData.scooter?.battery);
      setTripData(initialTripData);
      setHistoryPage(1);
    }
  }, [initialTripData]);

  useEffect(() => {
    if (activeTab === 2 && tripData?.user?._id && props.open) {
      dispatch(
        getContractsHistory({
          userId: tripData.user._id,
          page: historyPage,
          limit: 10,
          active: false
        })
      );
    }
  }, [dispatch, activeTab, tripData?.user?._id, historyPage, props.open]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    setActiveTab(0);
    setHistoryPage(1);
    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleCopyCoordinates = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSnackbarOpen(true);
    }).catch((err) => {
      console.error('Failed to copy coordinates:', err);
    });
  };

  const getStatusChip = () => {
    const status = tripData?.status?.value?.toLowerCase();
    let label = t('trips.details.tripCompletedShort');
    let color: 'success' | 'warning' | 'error' | 'default' = 'success';

    if (status === 'normal') {
      label = t('trips.details.inTrip');
      color = 'error';
    } else if (status === 'pause') {
      label = t('trips.details.pause');
      color = 'warning';
    } else if (status === 'reservation') {
      label = t('trips.details.booking');
      color = 'default';
    }

    return <Chip label={label} color={color} size="small" sx={{ borderRadius: '8px' }} />;
  };

  const calculateBatteryUsed = () => {
    const batteryStart = tripData?.historyDetails?.batteryStart !== undefined 
      ? tripData.historyDetails.batteryStart 
      : tripData?.contractData?.historyDetails?.batteryStart;
    const batteryEnd = tripData?.historyDetails?.batteryEnd !== undefined
      ? tripData.historyDetails.batteryEnd
      : tripData?.contractData?.historyDetails?.batteryEnd;
    
    if (batteryStart === undefined || batteryEnd === undefined) {
      return 0;
    }
    
    const used = batteryStart - batteryEnd;
    return Math.max(0, Math.min(100, used));
  };

  const renderMainInfo = () => {
    if (!tripData) return null;

    return (
      <Grid container spacing={3}>
        {/* Інформація про поїздку - ліворуч */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {t('trips.details.tripInfo')}
            </Typography>
            <InfoRow label={t('trips.details.status')} value={getStatusChip()} />
            <InfoRow label={t('trips.details.scooter')} value={tripData.scooter?.name || '—'} />
            <InfoRow
              label={t('trips.details.started')}
              value={formatDate(tripData.createdAt, { time: true })}
            />
            <InfoRow
              label={t('trips.details.ended')}
              value={tripData.closedDate ? formatDate(tripData.closedDate, { time: true }) : '—'}
            />
            <InfoRow
              label={t('trips.details.duration')}
              value={
                tripData.closedDate
                  ? moment
                      .utc(moment(tripData.closedDate).diff(moment(tripData.createdAt)))
                      .format('HH:mm:ss')
                  : '—'
              }
            />
            {tripData.startCoords && (
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 18, color: '#4CAF50' }} />}
                label={t('trips.details.startPoint')}
                value={`${tripData.startCoords.lat.toFixed(6)}, ${tripData.startCoords.lon.toFixed(6)}`}
                copyValue={`${tripData.startCoords.lat.toFixed(6)}, ${tripData.startCoords.lon.toFixed(6)}`}
                onCopy={handleCopyCoordinates}
              />
            )}
            {tripData.exitCoords && (
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 18, color: '#F44336' }} />}
                label={t('trips.details.endPoint')}
                value={`${tripData.exitCoords.lat.toFixed(6)}, ${tripData.exitCoords.lon.toFixed(6)}`}
                copyValue={`${tripData.exitCoords.lat.toFixed(6)}, ${tripData.exitCoords.lon.toFixed(6)}`}
                onCopy={handleCopyCoordinates}
              />
            )}
            <InfoRow label={t('trips.details.distance')} value={`${tripData.mileage || 0} ${t('trips.km')}`} />
            <InfoRow 
              label={t('trips.details.cost')} 
              value={
                tripData.sum 
                  ? `${tripData.sum} ₴` 
                  : tripData.contractData?.sum 
                    ? `${tripData.contractData.sum} ₴` 
                    : '—'
              } 
            />
            <InfoRow
              label={t('trips.details.rating')}
              value={
                tripData.contractReview?.rate ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {[...Array(5)].map((_, index) => (
                      <span
                        key={index}
                        style={{
                          color: index < tripData.contractReview.rate ? '#5073F8' : '#E0E0E0'
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </Box>
                ) : (
                  '—'
                )
              }
            />
            
            {onRefundClick && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AttachMoney />}
                  disabled={tripData.refundStatus === 'refunded' || tripData.refund === true}
                  onClick={() => onRefundClick(
                    tripData._id,
                    tripData.user?._id || '',
                    typeof tripData.sum === 'string' ? parseFloat(tripData.sum) : tripData.sum || 0,
                    tripData.historyDetails
                  )}
                  sx={{
                    backgroundColor: tripData.refundStatus === 'refunded' || tripData.refund === true 
                      ? '#cccccc' 
                      : '#4CAF50',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: tripData.refundStatus === 'refunded' || tripData.refund === true
                        ? '#cccccc'
                        : '#45a049'
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc',
                      color: '#666666'
                    }
                  }}
                >
                  {tripData.refundStatus === 'refunded' || tripData.refund === true 
                    ? t('trips.details.fundsRefunded') 
                    : t('trips.details.refundFunds')}
                </Button>
              </Box>
            )}

            {onEditReportAmount && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EuroSymbol />}
                  onClick={() => onEditReportAmount(tripData)}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    borderColor: '#4ECDC4',
                    color: theme.palette.mode === 'light' ? '#3BA99F' : '#4ECDC4',
                    '&:hover': {
                      borderColor: '#3BA99F',
                      backgroundColor: 'rgba(78, 205, 196, 0.08)'
                    }
                  }}
                >
                  {tripData.parkingContractsStatsAmount != null
                    ? t('trips.reportAmountValue', {
                        amount: tripData.parkingContractsStatsAmount
                      })
                    : t('trips.editReportAmount')}
                </Button>
              </Box>
            )}
          </InfoCard>
        </Grid>

        {/* Карта маршруту - праворуч в першому ряду */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {t('trips.details.routeMap')}
            </Typography>
            <Box sx={{ height: '400px', borderRadius: '12px', overflow: 'hidden' }}>
              {cityCoords && tripId ? (
                <TripCoordinatesHistory mapCenter={cityCoords} tripId={tripId} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5'
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    {t('trips.details.noRouteData')}
                  </Typography>
                </Box>
              )}
            </Box>
          </InfoCard>
        </Grid>

        {/* Інформація про користувача - ліворуч в другому ряду */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {t('trips.details.userInfo')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#E0E0E0',
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  mr: 2,
                  borderRadius: '8px'
                }}
              >
                {(tripData.user?.firstName?.charAt(0) || t('trips.details.unknownFirstName').charAt(0))}
                {(tripData.user?.lastName?.charAt(0) || t('trips.details.unknownLastName').charAt(0))}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {tripData.user?.firstName || t('trips.details.unknownFirstName')} {tripData.user?.lastName || t('trips.details.unknownLastName')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('trips.details.userRole')}
                </Typography>
              </Box>
            </Box>
            <InfoRow
              label={t('trips.details.phone')}
              icon={<Phone fontSize="small" />}
              value={tripData.user?.phone || '—'}
            />
            <InfoRow
              label={t('trips.details.status')}
              value={
                tripData.user?.active !== false ? (
                  <Chip
                    label={t('trips.details.active')}
                    color="success"
                    size="small"
                    sx={{ borderRadius: '8px' }}
                  />
                ) : (
                  <Chip
                    label={t('trips.details.inactive')}
                    color="error"
                    size="small"
                    sx={{ borderRadius: '8px' }}
                  />
                )
              }
            />
          </InfoCard>
        </Grid>

        {/* Фото після поїздки - праворуч в другому ряду */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {t('trips.details.tripPhoto')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#E0E0E0',
                borderRadius: '8px',
                position: 'relative',
                cursor: tripData.cableImg ? 'pointer' : 'default',
                transition: 'transform 0.2s',
                '&:hover': tripData.cableImg ? {
                  transform: 'scale(1.02)'
                } : {}
              }}
              onClick={() => tripData.cableImg && setLightboxOpen(true)}
            >
              {tripData.cableImg ? (
                <img
                  src={tripData.cableImg}
                  alt="Trip photo"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    borderRadius: '8px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <CameraAlt sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2">{t('trips.details.noPhoto')}</Typography>
                </Box>
              )}
            </Box>
            {tripData.cableImgCoords && (tripData.cableImgCoords.lat || tripData.cableImgCoords.lon) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('trips.details.coords')}:
                  </Typography>
                  <Tooltip title={t('trips.details.copy')}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyCoordinates(`${tripData.cableImgCoords.lat || '—'}, ${tripData.cableImgCoords.lon || '—'}`)}
                      sx={{
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ContentCopy sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body1" sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? '#1E88E5' : '#E3F2FD',
                  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#1976D2',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}>
                  {tripData.cableImgCoords.lat || '—'}
                  <br />
                  {tripData.cableImgCoords.lon || '—'}
                </Typography>
              </Box>
            )}
          </InfoCard>
        </Grid>
      </Grid>
    );
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getUserInitial = (user: any) => {
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return 'К';
  };

  const renderUserHistory = () => {
    if (!tripData?.user?._id) return null;

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} mb={3}>
          {t('trips.details.userHistory')} {tripData.user.firstName} {tripData.user.lastName}
        </Typography>

        <TableContainer 
          sx={{ 
            maxHeight: 500,
            backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
            borderRadius: '12px',
            mb: 2
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colScooter')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colStart')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colEnd')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colOnRoad')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colPause')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colReservation')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colDistance')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('trips.details.colCost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary" py={4}>
                      {t('trips.details.noHistory')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                historyContracts.map(contract => (
                  <TableRow key={contract._id}>
                    <TableCell>{contract.scooter?.name || '—'}</TableCell>
                    <TableCell>
                      {moment(contract.createdAt).format('DD.MM.YYYY HH:mm')}
                    </TableCell>
                    <TableCell>
                      {contract.closedDate 
                        ? moment(contract.closedDate).format('DD.MM.YYYY HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {contract?.historyDetails?.normal?.duration
                        ? convertSecondsToHHMMSS(contract.historyDetails.normal.duration)
                        : '00:00:00'}
                    </TableCell>
                    <TableCell>
                      {contract?.historyDetails?.pause?.duration
                        ? convertSecondsToHHMMSS(contract.historyDetails.pause.duration)
                        : '00:00:00'}
                    </TableCell>
                    <TableCell>
                      {contract?.historyDetails?.reservation?.duration
                        ? convertSecondsToHHMMSS(contract.historyDetails.reservation.duration)
                        : '00:00:00'}
                    </TableCell>
                    <TableCell>{contract.mileage || 0} {t('trips.km')}</TableCell>
                    <TableCell>{contract.sum || 0} ₴</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {historyPages > 1 && (
          <Box display="flex" justifyContent="center">
            <Pagination
              count={historyPages}
              page={historyPage}
              onChange={(event, nextPage) => setHistoryPage(nextPage)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  borderRadius: '8px'
                },
                '& .Mui-selected': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#5073F8 !important' : '#424242 !important',
                  color: theme.palette.mode === 'dark' ? '#000000 !important' : '#FFFFFF !important'
                }
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderTimeline = () => {
    if (!tripData) return null;

    const historyDetails = tripData.historyDetails;
    // Use actual duration in seconds for accurate proportions
    const reservationDuration = historyDetails?.reservation?.duration || 0;
    const normalDuration = historyDetails?.normal?.duration || 0;
    // Calculate stop/closure duration from timestamps if available
    const closureDuration = tripData.closedDate && tripData.createdAt
      ? Math.max(0, moment(tripData.closedDate).diff(moment(tripData.createdAt), 'seconds') - reservationDuration - normalDuration)
      : 0;

    // Use actual durations for flex proportions - they reflect real time ratios
    // Only use minimum of 1 to ensure div is visible even if 0 seconds
    const displayReservation = Math.max(reservationDuration, 1);
    const displayNormal = Math.max(normalDuration, 1);
    const displayClosure = Math.max(closureDuration, 1);

    const reservationMinutes = Math.floor(reservationDuration / 60);
    const normalMinutes = Math.floor(normalDuration / 60);
    
    const closedTime = tripData.closedDate
      ? moment(tripData.closedDate).format('HH:mm')
      : '';

    const startTime = tripData.createdAt
      ? moment(tripData.createdAt).format('HH:mm')
      : '';

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} mb={3}>
          {t('trips.details.timeline')}
        </Typography>

        {/* Timeline Bar */}
        <Box
          sx={{
            display: 'flex',
            height: '40px',
            borderRadius: '20px',
            overflow: 'hidden',
            mb: 4,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? '#424242' : '#E0E0E0'
          }}
        >
          {reservationDuration > 0 && (
            <Box
              sx={{
                flex: displayReservation,
                backgroundColor: '#1976D2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="caption" fontWeight={600} color="#FFFFFF">
                {t('trips.details.reservation')}
              </Typography>
            </Box>
          )}
          {normalDuration > 0 && (
            <Box
              sx={{
                flex: displayNormal,
                backgroundColor: '#4CAF50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="caption" fontWeight={600} color="#FFFFFF">
                {t('trips.details.start')}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              flex: displayClosure,
              backgroundColor: '#F44336',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#FFFFFF">
              {t('trips.details.stop')}
            </Typography>
          </Box>
        </Box>

        {/* Timeline Steps */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 4 }}>
          {reservationMinutes > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  backgroundColor: '#1976D2',
                  margin: '0 auto',
                  mb: 2
                }}
              >
                <LockClock sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="body1" fontWeight={600} mb={0.5}>
                {t('trips.details.scooterReserved')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {reservationMinutes} {t('trips.details.minutes')}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {startTime}
              </Typography>
            </Box>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#4CAF50',
                margin: '0 auto',
                mb: 2
              }}
            >
              <PlayCircle sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="body1" fontWeight={600} mb={0.5}>
              {t('trips.details.tripStarted')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {normalMinutes} {t('trips.details.minutes')}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {startTime}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#F44336',
                margin: '0 auto',
                mb: 2
              }}
            >
              <StopCircle sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="body1" fontWeight={600} mb={0.5}>
              {t('trips.details.tripEnded')}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {closedTime}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      {...restProps}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {tripData && getStatusChip()}
            <Typography variant="h6" fontWeight={600}>
              {t('trips.details.tripHeader')} #{tripData?._id?.slice(-8) || ''} • {tripData?.scooter?.name || 'ECO-'}
            </Typography>
          </Box>
          <IconButton onClick={() => handleClose({}, 'escapeKeyDown')} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {t('trips.details.started')}: {tripData?.createdAt ? formatDate(tripData.createdAt, { time: true }) : ''} •{' '}
          {tripData?.status?.value?.toLowerCase() === 'exit' ? t('trips.details.tripCompletedShort') : t('trips.details.tripInProgress')}
        </Typography>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 3,
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'dark' ? '#5073F8' : '#424242',
              height: 3
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              minHeight: 48,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242'
              }
            }
          }}
        >
          <Tab label={t('trips.details.tabMain')} />
          <Tab label={t('trips.details.tabTimeline')} />
          <Tab label={t('trips.details.tabHistory')} />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography variant="body1" color="textSecondary">
              {t('trips.details.loading')}
            </Typography>
          </Box>
        ) : (
          <>
            <TabPanel value={activeTab} index={0}>
              {renderMainInfo()}
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              {renderTimeline()}
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              {renderUserHistory()}
            </TabPanel>
          </>
        )}
      </DialogContent>

      {/* Lightbox for full-screen photo */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            borderRadius: 0,
            m: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100vw',
            height: '100vh'
          }
        }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#FFFFFF',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)'
            },
            zIndex: 1
          }}
        >
          <Close />
        </IconButton>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={() => setLightboxOpen(false)}
        >
          {tripData?.cableImg && (
            <img
              src={tripData.cableImg}
              alt="Trip photo fullscreen"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* Snackbar для підтвердження копіювання */}
      <Snackbar
        open={copiedSnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopiedSnackbarOpen(false)}
        message={t('trips.copied')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};
